import {
  mergeWorkoutLayers,
  MetricBenchmarkData,
} from '@strength/domain-workout';
import { useMemo } from 'react';
import { trpc } from '../../../../../trpc/react';
import { useBenchmarks } from '../../../../hooks/useBenchmarks';
import { SingleWorkoutEditContext } from '../../../block/block-all/SingleWorkoutSheet';
import { WorkoutWithCycleAndSession } from '../../../block/types';
import {
  buildPreviewEditContext,
  toPreviewWorkout,
} from '../components/workout-preview-adapter';

export type UseSingleWorkoutResult = {
  workout: WorkoutWithCycleAndSession | null;
  editContext: SingleWorkoutEditContext | null;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Fetches and merges the published layers for a single workout, plus the
 * athlete-benchmark data needed to resolve `%` metrics, into the shapes
 * `SingleWorkoutSheet` renders. `workout`/`editContext` resolve together
 * (both null until the main query loads) since they're built from the same
 * merged data.
 */
export function useSingleWorkout({
  blockId,
  workoutId,
  profileId,
  sourceTeamId,
}: {
  blockId: string | null;
  workoutId: string | null;
  profileId: string | null;
  /** Team owning the block when previewing an additional team's workout. */
  sourceTeamId?: string | null;
}): UseSingleWorkoutResult {
  const { defaultBenchmarkGridId } = useBenchmarks();

  const enabled = !!blockId && !!workoutId;
  const benchmarksEnabled = enabled && !!profileId;

  const { data, isLoading, isError } = trpc.block.getWorkoutPreview.useQuery(
    {
      blockId: blockId ?? '',
      workoutId: workoutId ?? '',
      profileId: profileId ?? undefined,
      sourceTeamId: sourceTeamId ?? undefined,
    },
    { enabled },
  );

  // Per-athlete benchmark resolution (SC-3344 / SC-3837 pattern) — empty when
  // no athlete is selected, leaving prescribed `%` values literal.
  const { data: currentExerciseBenchmarks, isLoading: benchmarksLoading } =
    trpc.athleteBenchmarks.fetchAllExerciseBenchmarksForAthlete.useQuery(
      { profileId: profileId ?? '' },
      { enabled: benchmarksEnabled, staleTime: 10_000 },
    );
  const { data: maxConversions, isLoading: maxConversionsLoading } =
    trpc.exerciseMaxConversionLibrary.allExerciseMaxConversions.useQuery(
      undefined,
      { enabled: benchmarksEnabled, staleTime: 10_000 },
    );
  const { data: kpiBenchmarks, isLoading: kpiBenchmarksLoading } =
    trpc.athleteKpiBenchmarks.fetchKpiBenchmarksForAthlete.useQuery(
      { profileId: profileId ?? '' },
      { enabled: benchmarksEnabled, staleTime: 10_000 },
    );

  // The main query resolving doesn't mean the preview is ready to render: the
  // benchmark queries above resolve `%`-based metric values via
  // `applyBenchmarkData`, so we stay "loading" until whichever of them are
  // actually enabled have also settled, otherwise the drawer briefly shows
  // unresolved/literal `%` values that then jump to resolved ones.
  const combinedIsLoading =
    isLoading ||
    (benchmarksEnabled && benchmarksLoading) ||
    (benchmarksEnabled && maxConversionsLoading) ||
    (benchmarksEnabled && kpiBenchmarksLoading);

  const applyBenchmarkData: MetricBenchmarkData = useMemo(
    () => ({
      currentExerciseBenchmarks: currentExerciseBenchmarks ?? [],
      maxConversions: maxConversions ?? [],
      // getWorkoutPreview does not return the parent Block, so the ratio the
      // coach configured isn't available here; 1 (no adjustment) matches the
      // fallback used elsewhere when the Block isn't loaded (SC-3838).
      trainingMaxRatio: 1,
      defaultBenchmarkGridId,
      kpiBenchmarks: kpiBenchmarks ?? {},
    }),
    [
      currentExerciseBenchmarks,
      maxConversions,
      defaultBenchmarkGridId,
      kpiBenchmarks,
    ],
  );

  return useMemo(() => {
    if (!data)
      return {
        workout: null,
        editContext: null,
        isLoading: combinedIsLoading,
        isError,
      };
    const merged = mergeWorkoutLayers(
      data.blockLayer,
      data.athleteLayer ?? undefined,
      data.modifiedLayer ?? undefined,
    );
    const blockMeta = {
      cycleSortOrder: data.blockLayer.cycleSortOrder,
      sessionSortOrder: data.blockLayer.sessionSortOrder,
      status: data.blockLayer.status,
    };
    return {
      workout: toPreviewWorkout(merged, blockMeta),
      editContext: buildPreviewEditContext(applyBenchmarkData, profileId),
      isLoading: combinedIsLoading,
      isError,
    };
  }, [data, applyBenchmarkData, profileId, combinedIsLoading, isError]);
}
